import { useEffect, useRef, useState } from "react"

import { Badge } from "@/components/base/badges/badges"
import AssessorView from "@/components/chat/AssessorView"
import ChatCoachingSurface from "@/components/chat/CoachingSurface"
import DebriefSurface from "@/components/chat/DebriefSurface"
import DoorsTracker from "@/components/chat/DoorsTracker"
import SimulationSurface from "@/components/chat/SimulationSurface"
import { NO_COACHING_MESSAGE } from "@/constants/coaching"
import {
  type ConversationState,
  ConversationStateBuilder,
  type MinigameState,
} from "@/utils/conversation-state-builder"

import { useReplay } from "./ReplayProvider"

function StageIndicator({ stage }: { stage: string }) {
  const stageColors: Record<
    string,
    | "gray"
    | "brand"
    | "error"
    | "warning"
    | "success"
    | "gray-blue"
    | "blue-light"
    | "blue"
    | "indigo"
    | "purple"
    | "pink"
    | "orange"
  > = {
    bootstrapping: "gray",
    goal_selection: "blue",
    pace_presenting: "success",
    pace_asking: "purple",
    pace_checking: "orange",
    pace_expressing: "pink",
    debrief: "warning",
    session_complete: "gray",
  }

  // Human-readable, jargon-free stage labels for the funder-facing surface.
  // PACE / species-rule names are kept off the trainee thread entirely; "PACE"
  // is introduced once, late, in the debrief as credibility.
  const stageLabels: Record<string, string> = {
    bootstrapping: "Getting started",
    goal_selection: "Choosing a focus",
    pace_presenting: "Describing how you feel",
    pace_asking: "Asking what you need",
    pace_checking: "Checking you understood",
    pace_expressing: "Saying what you want",
    debrief: "Session debrief",
    session_complete: "Complete",
  }

  const color = stageColors[stage] || "gray"

  return (
    <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
        Current Stage
      </span>
      <Badge type="pill-color" color={color} size="md">
        {stageLabels[stage] ?? stage.replace(/_/g, " ")}
      </Badge>
    </div>
  )
}

function MinigameDisplay({ game }: { game: MinigameState }) {
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-700 dark:bg-indigo-900/20">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-indigo-900 dark:text-indigo-300">
          Minigame: {game.type?.replace(/_/g, " ")}
        </span>
        {game.completed && (
          <Badge
            type="pill-color"
            color={game.success ? "success" : "error"}
            size="sm"
          >
            {game.success ? "Success" : "Failed"}
          </Badge>
        )}
      </div>
      {game.score !== undefined && (
        <p className="text-sm text-indigo-700 dark:text-indigo-300">
          Score: {Math.round(game.score * 100)}%
        </p>
      )}
    </div>
  )
}

// Typing indicator is provided by a shared component in chat surfaces when needed.

export function ConversationView() {
  const { events, currentEventIndex } = useReplay()
  const [conversationState, setConversationState] =
    useState<ConversationState | null>(null)
  const stateBuilder = useRef(new ConversationStateBuilder())

  // Rebuild state up to current event
  useEffect(() => {
    if (events.length === 0) return

    const state = stateBuilder.current.applyEventsUpTo(
      events,
      currentEventIndex,
    )
    setConversationState(state)
  }, [events, currentEventIndex])

  // In replay, message scrolling is handled by the embedded SimulationSurface

  if (!conversationState) {
    return (
      <div className="conversation-view rounded-lg bg-white p-6 shadow-sm dark:bg-gray-900">
        <div className="flex h-96 items-center justify-center">
          <p className="text-gray-500 dark:text-gray-400">
            Loading conversation...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="conversation-view space-y-6 rounded-xl bg-white p-8 shadow-md dark:bg-gray-900">
      {/* Stage Indicator */}
      <StageIndicator stage={conversationState.stage} />

      {/* Doors opened — the literal "open doors to getting help" progress. The
          scrubber drives playback, so tooltips don't auto-announce here. */}
      <DoorsTracker opened={conversationState.doors} announce={false} />

      {/* Assessor view — admin-only, toggle-gated, self-nulls when off */}
      <AssessorView />

      {/* Debrief card — the hero payoff, shown once the arc reaches the end */}
      {(conversationState.stage === "debrief" ||
        conversationState.stage === "session_complete") &&
        conversationState.debrief && (
          <DebriefSurface debrief={conversationState.debrief} />
        )}

      {/* Coaching Surface (always present, read-only) */}
      <ChatCoachingSurface
        userName="You"
        isExpanded={!!conversationState.coaching.visible}
        readOnly
        readonlyContent={
          (conversationState.coaching.content ?? "") || NO_COACHING_MESSAGE
        }
        mode={conversationState.coaching.mode}
        currentStep={Math.max((conversationState.coaching.page ?? 1) - 1, 0)}
        totalSteps={conversationState.coaching.total_pages ?? 1}
        allowSkip={false}
      />

      {/* Active Minigame */}
      {conversationState.minigame && (
        <MinigameDisplay game={conversationState.minigame} />
      )}

      {/* Message Thread (read-only variant of the chat component) */}
      <div className="rounded-lg">
        {/** Show only events up to the current index during replay */}
        {/** This yields progressive chat playback in realtime */}
        <SimulationSurface
          conversationId={undefined}
          events={(events || []).slice(
            0,
            Math.min(currentEventIndex + 1, events.length),
          )}
          // Replay is pure history — every beat is a recorded event, so the
          // transcript paints statically as the scrubber advances (no live
          // reveal ceremony). null = "no live events".
          liveCursor={null}
          isFocused
          isBlurred={false}
          readOnly
        />
      </div>
    </div>
  )
}
